import { useEffect, useMemo, useState } from "react";
import {
  FormCard,
  FormDialog,
  FormSection,
  TableKit,
} from "@NP-Webber/crm-kit";
import "@NP-Webber/crm-kit/dist/formkit/FormKit.css";
import "@NP-Webber/crm-kit/dist/tablekit/TableKit.css";
import {
  shellApi,
  type ForumDashboardDetail,
  type ForumDashboardDetailKind,
  type ForumDebtFund,
  type ForumDebtMonth,
} from "../api/shellApi";
import { formatShekels } from "../api/financeApi";
import { toClientApiError } from "../api/httpClient";
import "./forumDashboard.css";

type Props = {
  kind: ForumDashboardDetailKind | null;
  onClose: () => void;
};

const TITLES: Record<ForumDashboardDetailKind, string> = {
  open_debts: "פירוט חובות פתוחים",
  credit_balances: "יתרות זכות",
  active_funds: "קופות פעילות",
  active_subscriptions: "מנויים פעילים",
  expiring_subscriptions: "מנויים פוקעים (30 יום)",
};

function fmtCurrency(agorot: number): string {
  return `${formatShekels(agorot)} ₪`;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("he-IL");
}

function fmtDebtBalance(agorot: number): { text: string; className: string } {
  if (agorot > 0) {
    return {
      text: `חייבים ${fmtCurrency(agorot)}`,
      className: "forum-kpi-detail__debt-owing",
    };
  }
  if (agorot < 0) {
    return {
      text: `שולם ביתר ${fmtCurrency(Math.abs(agorot))}`,
      className: "forum-kpi-detail__debt-overpaid",
    };
  }
  return { text: "מאוזן", className: "forum-kpi-detail__muted" };
}

function MonthTable({ months }: { months: ForumDebtMonth[] }) {
  if (months.length === 0) {
    return <p className="forum-kpi-detail__muted">אין תנועות לפי חודש</p>;
  }
  return (
    <table className="forum-kpi-detail__months">
      <thead>
        <tr>
          <th>חודש</th>
          <th>+ חיובים</th>
          <th>− תשלומים</th>
          <th>יתרת חוב</th>
        </tr>
      </thead>
      <tbody>
        {months.map((m) => {
          const bal = fmtDebtBalance(m.netAgorot);
          return (
            <tr key={m.period}>
              <td>{m.period}</td>
              <td className="forum-kpi-detail__plus">+{fmtCurrency(m.chargesAgorot)}</td>
              <td className="forum-kpi-detail__minus">−{fmtCurrency(m.paymentsAgorot)}</td>
              <td className={bal.className}>{bal.text}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function OpenDebtsView({
  items,
  totalDebtAgorot,
  debtorCount,
}: {
  items: ForumDebtFund[];
  totalDebtAgorot: number;
  debtorCount: number;
}) {
  const [openIds, setOpenIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (items.length > 0 && items.length <= 5) {
      setOpenIds(new Set(items.map((i) => i.customerId)));
    }
  }, [items]);

  function toggle(id: string) {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="forum-kpi-detail__debts">
      <p className="forum-kpi-detail__summary">
        סה״כ חוב {fmtCurrency(totalDebtAgorot)} · {debtorCount} קופות חייבות לפורום
      </p>
      <p className="forum-kpi-detail__hint">
        חוב = הקופה חייבת לפורום. «יתרת חוב» בכל שורה = כמה חייבים מחיובים פחות
        תשלומים באותו חודש (+ חיוב / − תשלום).
      </p>
      {items.length === 0 ? (
        <p className="forum-kpi-detail__muted">אין קופות בחוב</p>
      ) : (
        <ul className="forum-kpi-detail__fund-list">
          {items.map((fund) => {
            const open = openIds.has(fund.customerId);
            return (
              <li key={fund.customerId} className="forum-kpi-detail__fund">
                <button
                  type="button"
                  className="forum-kpi-detail__fund-head"
                  onClick={() => toggle(fund.customerId)}
                  aria-expanded={open}
                >
                  <span className="forum-kpi-detail__fund-title">
                    {fund.fundName}
                    <span className="forum-kpi-detail__code">
                      #{String(fund.fundCode).padStart(6, "0")}
                    </span>
                  </span>
                  <span className="forum-kpi-detail__fund-balance">
                    יתרת חוב {fmtCurrency(fund.balanceAgorot)}
                  </span>
                </button>
                {open ? (
                  <div className="forum-kpi-detail__fund-body">
                    {(fund.phone || fund.email) && (
                      <p className="forum-kpi-detail__extra">
                        {fund.phone ? <span>טלפון: {fund.phone}</span> : null}
                        {fund.phone && fund.email ? " · " : null}
                        {fund.email ? <span>אימייל: {fund.email}</span> : null}
                      </p>
                    )}
                    {fund.subscriptions.length === 0 &&
                    fund.unassignedMonths.length === 0 ? (
                      <p className="forum-kpi-detail__muted">אין מנויים או תנועות</p>
                    ) : null}
                    {fund.subscriptions.map((sub) => (
                      <div key={sub.subscriptionId} className="forum-kpi-detail__sub">
                        <div className="forum-kpi-detail__sub-head">
                          <strong>{sub.title}</strong>
                          <span>{fmtCurrency(sub.amountAgorot)}</span>
                        </div>
                        <p className="forum-kpi-detail__extra">
                          {[
                            sub.categoryName ? `אגף: ${sub.categoryName}` : null,
                            `סטטוס: ${sub.status}`,
                            sub.startsOn ? `התחלה: ${fmtDate(sub.startsOn)}` : null,
                            sub.endsOn ? `סיום: ${fmtDate(sub.endsOn)}` : null,
                            sub.paymentArrangement
                              ? `הסדר: ${sub.paymentArrangement}`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                        <MonthTable months={sub.months} />
                      </div>
                    ))}
                    {fund.unassignedMonths.length > 0 ? (
                      <div className="forum-kpi-detail__sub">
                        <div className="forum-kpi-detail__sub-head">
                          <strong>ללא מנוי</strong>
                        </div>
                        <MonthTable months={fund.unassignedMonths} />
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function ForumKpiDetailDialog({ kind, onClose }: Props) {
  const open = Boolean(kind);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<ForumDashboardDetail | null>(null);

  useEffect(() => {
    if (!kind) {
      setDetail(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    void shellApi
      .getForumDashboardDetail(kind)
      .then((item) => {
        if (!cancelled) setDetail(item);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(toClientApiError(err).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [kind]);

  const fundColumns = useMemo(
    () => [
      { key: "name", title: "קופה", sortable: true, minWidth: 160 },
      {
        key: "code",
        title: "קוד",
        sortable: true,
        minWidth: 90,
        render: (v: unknown) => String(Number(v) || 0).padStart(6, "0"),
      },
      { key: "phone", title: "טלפון", sortable: true, minWidth: 110 },
      { key: "email", title: "אימייל", sortable: true, minWidth: 140 },
      {
        key: "balanceAgorot",
        title: "יתרה",
        sortable: true,
        minWidth: 100,
        render: (v: unknown) => fmtCurrency(Number(v) || 0),
      },
      {
        key: "activeSubscriptionCount",
        title: "מנויים פעילים",
        sortable: true,
        minWidth: 110,
      },
    ],
    [],
  );

  const creditColumns = useMemo(
    () => [
      { key: "fundName", title: "קופה", sortable: true, minWidth: 160 },
      {
        key: "fundCode",
        title: "קוד",
        sortable: true,
        minWidth: 90,
        render: (v: unknown) => String(Number(v) || 0).padStart(6, "0"),
      },
      { key: "phone", title: "טלפון", sortable: true, minWidth: 110 },
      { key: "email", title: "אימייל", sortable: true, minWidth: 140 },
      {
        key: "creditAgorot",
        title: "יתרת זכות",
        sortable: true,
        minWidth: 120,
        render: (v: unknown) => (
          <span className="forum-kpi-detail__credit">{fmtCurrency(Number(v) || 0)}</span>
        ),
      },
    ],
    [],
  );

  const subColumns = useMemo(
    () => [
      { key: "fundName", title: "קופה", sortable: true, minWidth: 140 },
      { key: "title", title: "מנוי", sortable: true, minWidth: 140 },
      { key: "categoryName", title: "אגף", sortable: true, minWidth: 100 },
      {
        key: "amountAgorot",
        title: "סכום",
        sortable: true,
        minWidth: 100,
        render: (v: unknown) => fmtCurrency(Number(v) || 0),
      },
      {
        key: "startsOn",
        title: "התחלה",
        sortable: true,
        minWidth: 100,
        render: (v: unknown) => fmtDate(v ? String(v) : null),
      },
      {
        key: "endsOn",
        title: "סיום",
        sortable: true,
        minWidth: 100,
        render: (v: unknown) => fmtDate(v ? String(v) : null),
      },
      {
        key: "daysUntilEnd",
        title: "ימים לסיום",
        sortable: true,
        minWidth: 100,
        render: (v: unknown) => (v == null || v === "" ? "—" : String(v)),
      },
      {
        key: "paymentArrangement",
        title: "הסדר",
        sortable: true,
        minWidth: 100,
        render: (v: unknown) => (v ? String(v) : "—"),
      },
    ],
    [],
  );

  const title = kind ? TITLES[kind] : "";

  return (
    <FormDialog open={open} onClose={onClose} size="lg">
        <FormCard
          title={title}
          error={error}
          onCancel={onClose}
          cancelLabel="סגור"
        >
        <FormSection>
          {loading ? (
            <p className="forum-kpi-detail__muted">טוען פירוט…</p>
          ) : detail?.kind === "open_debts" ? (
            <OpenDebtsView
              items={detail.items}
              totalDebtAgorot={detail.totalDebtAgorot}
              debtorCount={detail.debtorCount}
            />
          ) : detail?.kind === "credit_balances" ? (
            <div className="forum-kpi-detail__table">
              <p className="forum-kpi-detail__summary">
                סה״כ זכות {fmtCurrency(detail.totalCreditAgorot)} · {detail.creditCount}{" "}
                קופות — הפורום חייב לקופות
              </p>
              <p className="forum-kpi-detail__hint">
                זכות = הפורום חייב לקופה (יתרה שלילית במערכת).
              </p>
              <TableKit
                columns={creditColumns}
                data={detail.items.map((r) => ({
                  id: r.customerId,
                  fundName: r.fundName,
                  fundCode: r.fundCode,
                  phone: r.phone ?? "—",
                  email: r.email ?? "—",
                  creditAgorot: r.creditAgorot,
                })) as unknown as Array<Record<string, unknown>>}
                clientSideMode
                emptyMessage="אין יתרות זכות"
                showFilters
                showColumnPicker={false}
                showAdvancedSettings={false}
                showExport={false}
              />
            </div>
          ) : detail?.kind === "active_funds" ? (
            <div className="forum-kpi-detail__table">
              <p className="forum-kpi-detail__summary">{detail.count} קופות</p>
              <TableKit
                columns={fundColumns}
                data={detail.items.map((r) => ({
                  id: r.customerId,
                  name: r.name,
                  code: r.code,
                  phone: r.phone ?? "—",
                  email: r.email ?? "—",
                  balanceAgorot: r.balanceAgorot,
                  activeSubscriptionCount: r.activeSubscriptionCount,
                })) as unknown as Array<Record<string, unknown>>}
                clientSideMode
                emptyMessage="אין קופות פעילות"
                showFilters
                showColumnPicker={false}
                showAdvancedSettings={false}
                showExport={false}
              />
            </div>
          ) : detail?.kind === "active_subscriptions" ||
            detail?.kind === "expiring_subscriptions" ? (
            <div className="forum-kpi-detail__table">
              <p className="forum-kpi-detail__summary">{detail.count} מנויים</p>
              <TableKit
                columns={subColumns}
                data={detail.items.map((r) => ({
                  id: r.id,
                  fundName: r.fundName,
                  title: r.title,
                  categoryName: r.categoryName ?? "—",
                  amountAgorot: r.amountAgorot,
                  startsOn: r.startsOn,
                  endsOn: r.endsOn,
                  daysUntilEnd: r.daysUntilEnd,
                  paymentArrangement: r.paymentArrangement,
                })) as unknown as Array<Record<string, unknown>>}
                clientSideMode
                emptyMessage="אין מנויים להצגה"
                showFilters
                showColumnPicker={false}
                showAdvancedSettings={false}
                showExport={false}
              />
            </div>
          ) : !loading && !error ? (
            <p className="forum-kpi-detail__muted">אין נתונים</p>
          ) : null}
        </FormSection>
      </FormCard>
    </FormDialog>
  );
}
